import { Box, Flex, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { ButtonText } from '@morressier/alembic';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import OrganizationSearchInput from './OrgonizationSearchInput';
import CustomCountrySelect from './CustomCountrySelect';
import CustomFormFieldTextInput from 'src/components/FormField/CustomFormFieldTextInput';
import { AffiliationCountryList } from 'src/api/queries';

const AffiliationAddressRow = ({
  idx,
  formConfig,
  onRemove,
  defaultValue,
}: {
  idx: number;
  fieldsLength: any;
  formConfig: any;
  onRemove: (index: number) => void;
  defaultValue: string;
}) => {
  const { setValue, watch, clearErrors } = useFormContext();
  const affiliation = watch(
    `additionalAffiliations.${idx}.selectedAffiliation`
  );

  const ringgoldId = watch(`additionalAffiliations.${idx}.ringgoldId`);

  const [selectedCountry, setSelectedCountry] =
    useState<AffiliationCountryList | null>(null);

  const resetData = () => {
    setValue(`additionalAffiliations.${idx}`, {
      organizationName: '',
      streetAddress: '',
      city: '',
      stateOrRegion: '',
      postalCode: '',
      country: '',
      countryCode: '',
      ringgoldId: '',
      url: '',
      selectedAffiliation: null,
      searchValue: '' // also clear the search value itself
    });
    setSelectedCountry(null);
  };

  useEffect(() => {
    if (affiliation) {
      setValue(
        `additionalAffiliations.${idx}.organizationName`,
        affiliation.name || ''
      );
      setValue(
        `additionalAffiliations.${idx}.streetAddress`,
        affiliation.formattedLocation || ''
      );
      setValue(
        `additionalAffiliations.${idx}.postalCode`,
        affiliation.postCode || ''
      );
      setValue(
        `additionalAffiliations.${idx}.stateOrRegion`,
        affiliation.state || ''
      );
      setValue(`additionalAffiliations.${idx}.city`, affiliation.city || '');
      setValue(
        `additionalAffiliations.${idx}.country`,
        affiliation.country || ''
      );
      setValue(
        `additionalAffiliations.${idx}.countryCode`,
        affiliation.countryCode || ''
      );
      setValue(
        `additionalAffiliations.${idx}.ringgoldId`,
        affiliation.ringgoldId ?? ''
      );
      setValue(
        `additionalAffiliations.${idx}.url`,
        affiliation.primaryDomain ?? ''
      );
      setSelectedCountry({
        country: affiliation.country ?? '',
        countryCode: affiliation.countryCode ?? ''
      });
      clearErrors(`additionalAffiliations.${idx}`)
    }
  }, [affiliation, idx, setValue]);

  useEffect(() => {
    const countryValue = watch(`additionalAffiliations.${idx}.country`);
    const countryCode = watch(`additionalAffiliations.${idx}.countryCode`);

    if (
      countryValue &&
      (!selectedCountry || selectedCountry.country !== countryValue)
    ) {
      setSelectedCountry({ country: countryValue, countryCode: countryCode });
    }
  }, [watch, idx, selectedCountry]);

  return (
    <Box mb={6} p={4} borderWidth={1} borderRadius="lg">
      <Flex justifyContent={'space-between'} alignItems="flex-start" gap={2} wrap="wrap">
        <Text textStyle={'headingItem'}>Secondary Affiliation(s) {idx + 1}</Text>
        <ButtonText
          variant="caution"
          mt={2}
          onClick={() => onRemove(idx)}
          iconLeft={<FontAwesomeIcon icon={faTrash} />}
        >
          Remove Affiliation
        </ButtonText>
      </Flex>
      <Flex direction={'column'} gap={3}>
        {formConfig.organizationName && (
          <Flex direction={'column'} gap={1}>
            <OrganizationSearchInput
              fieldPrefix={`additionalAffiliations.${idx}`}
              config={formConfig.organizationName}
              fieldName={`additionalAffiliations.${idx}.organizationName`}
              defaultValue={defaultValue}
              resetData={resetData}
              ringgoldId={ringgoldId}
            />
          </Flex>
        )}
        {formConfig.department && (
          <CustomFormFieldTextInput
            config={formConfig.department}
            fieldName={`additionalAffiliations.${idx}.department`}
          />
        )}

        {formConfig.streetAddress && (
          <CustomFormFieldTextInput
            config={formConfig.streetAddress}
            fieldName={`additionalAffiliations.${idx}.streetAddress`}
          />
        )}
        <Flex gap={4} flex={1} direction={{ base: 'column', md: 'row' }}>
          {formConfig.city && (
            <CustomFormFieldTextInput
              config={formConfig.city}
              fieldName={`additionalAffiliations.${idx}.city`}
            />
          )}
          {formConfig.stateOrRegion && (
            <CustomFormFieldTextInput
              config={formConfig.stateOrRegion}
              fieldName={`additionalAffiliations.${idx}.stateOrRegion`}
            />
          )}
          {formConfig.postalCode && (
            <CustomFormFieldTextInput
              config={formConfig.postalCode}
              fieldName={`additionalAffiliations.${idx}.postalCode`}
            />
          )}
        </Flex>
        {formConfig.country && (
          <CustomCountrySelect
            selectedCountry={selectedCountry}
            setSelectedCountry={countryObj => {
              setSelectedCountry(countryObj);
              setValue(
                `additionalAffiliations.${idx}.country`,
                countryObj?.country || ''
              );
              setValue(
                `additionalAffiliations.${idx}.countryCode`,
                countryObj?.countryCode || ''
              );
            }}
            fieldPrefix={`additionalAffiliations.${idx}`}
            config={formConfig.country}
          />
        )}
        {formConfig.url && (
          <CustomFormFieldTextInput
            config={formConfig.url}
            fieldName={`additionalAffiliations.${idx}.url`}
          />
        )}
      </Flex>
    </Box>
  );
};

export default AffiliationAddressRow;
